import {ELOTypeEnum, MatchmakingMatchTypes} from '@colonist/game-base-types'
import {FindGameRankedState} from '../../../../../shared/ranked/ranked_user_state_data/FindGameRankedState'
import {RankedUserStateController} from '@colonist/leaderboard-types'
import {UIAjaxWrapper} from '../../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../../shared/scripts/network/UIAPIEndpoints'
import {Sound} from '../../../../shared/scripts/Sound'
import {UIWAdController} from '../../ui_ad/UIWAdController'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {AnalyticEventValue, AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {ViewPopupLogin} from '../../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {ViewPopupRankedGameDescription} from '../../../../shared/scripts/ui_popup/ViewPopupRankedGameDescription.ts'
import {createDivIn, createImgIn, createLinkIn, setElementInnerHTML} from '../../../../shared/scripts/ui_utils/DomUtils'
import {UIManager} from '../UIManager'
import {TranslationObject} from '@colonist/i18n'
import {userHasAccessToRankedCitiesAndKnights} from '../../../../../shared/matchmaking/MatchmakingUtils'
import {LobbyImage} from '@colonist/types'
import {UserSessionUtils} from '../../../../shared/scripts/UserSessionUtils'
import {navigateToPage} from '../../../../shared/scripts/ui_utils/ClientUtils.ts'

export class UIMainPageRankedActionsController {
    uiManager: UIManager
    rankedInfoIcon: HTMLDivElement
    rankedFourPlayerCard: HTMLDivElement
    rankedOneVersusOneCard: HTMLDivElement
    rankedCitiesAndKnightsCard: HTMLDivElement
    findGameRankedStates: FindGameRankedState[] = []
    fourPlayerSpan: HTMLSpanElement
    oneVersusOneSpan: HTMLSpanElement
    citiesAndKnightsSpan: HTMLSpanElement
    subscribeCTAButton: HTMLLinkElement | undefined
    hasAccessToCitiesAndKnights: boolean

    constructor(uiManager: UIManager) {
        this.uiManager = uiManager
        this.init()
        ClientUserState.registerChangeListener(() => {
            void this.updateWithUserState()
        })
    }

    private init() {
        this.fourPlayerSpan = document.getElementById('mainpage_ranked_card_4p_placement_games') as HTMLSpanElement
        this.oneVersusOneSpan = document.getElementById('mainpage_ranked_card_1v1_placement_games') as HTMLSpanElement
        this.citiesAndKnightsSpan = document.getElementById('mainpage_ranked_card_cities_and_knights_placement_games') as HTMLSpanElement

        this.rankedFourPlayerCard = document.getElementById('mainpage_ranked_card_4p') as HTMLDivElement
        this.rankedOneVersusOneCard = document.getElementById('mainpage_ranked_card_1v1') as HTMLDivElement
        this.rankedCitiesAndKnightsCard = document.getElementById('mainpage_ranked_card_CK') as HTMLDivElement

        this.rankedInfoIcon = document.getElementById('mainpage-ranked-card-info-icon') as HTMLDivElement
        this.rankedInfoIcon.onclick = () => new ViewPopupRankedGameDescription()

        this.setCardsClickHandler()

        this.initCKSubscribeCTA()
    }

    private async updateWithUserState() {
        this.resetToInitialView()

        if(!ClientUserState.userState?.isLoggedIn) {
            this.populateSignInLinks()
            return
        }

        const isEligibleForRanked = UserSessionUtils.userEligibleForRanked()
        this.hasAccessToCitiesAndKnights = userHasAccessToRankedCitiesAndKnights(ClientUserState.userState?.items)

        this.toggleCKSubscribeCTA(!this.hasAccessToCitiesAndKnights)

        if(!isEligibleForRanked) {
            this.setRankedIneligibleSubtext()
            UIMainPageRankedActionsController.addLockedImg(this.rankedFourPlayerCard)
            UIMainPageRankedActionsController.addLockedImg(this.rankedOneVersusOneCard)
            UIMainPageRankedActionsController.addLockedImg(this.rankedCitiesAndKnightsCard)
        } else {
            await this.fetchRankedState()
            this.populatePlacementGames()
            if(!this.hasAccessToCitiesAndKnights) UIMainPageRankedActionsController.addLockedImg(this.rankedCitiesAndKnightsCard)
        }
    }

    private resetToInitialView() {
        this.fourPlayerSpan.replaceChildren()
        this.oneVersusOneSpan.replaceChildren()
        this.citiesAndKnightsSpan.replaceChildren()

        const lockImages = document.querySelectorAll('#mainpage_ranked_cards_container .mainpage_ranked_card_locked_icon')
        lockImages.forEach((image) => image.remove())
    }

    async fetchRankedState() {
        const fetchUserState = ClientUserState.userState
        const displayError = false
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiFindGameState(), undefined, undefined, displayError)
        if(response == undefined) return
        if(fetchUserState !== ClientUserState.userState) return // user state was changed during fetch, ignore result
        this.findGameRankedStates = response.json as FindGameRankedState[]
    }

    private static addLockedImg(container: HTMLDivElement) {
        const lockDiv = createDivIn(container, 'mainpage_ranked_card_locked_icon_container')
        createImgIn(lockDiv, LobbyImage.LockSlanted, 'mainpage_ranked_card_locked_icon')
    }

    private setCardsClickHandler() {
        this.rankedFourPlayerCard.onclick = async() => {
            await this.handleCardClicked(MatchmakingMatchTypes.RankedClassic4P)
        }

        this.rankedOneVersusOneCard.onclick = async() => {
            await this.handleCardClicked(MatchmakingMatchTypes.RankedClassic1v1)
        }

        this.rankedCitiesAndKnightsCard.onclick = async() => {
            await this.handleCardClicked(MatchmakingMatchTypes.RankedCitiesAndKnights4P)
        }
    }

    private initCKSubscribeCTA() {
        this.subscribeCTAButton = document.getElementById('mainpage_ranked_ck_subscription_cta') as HTMLLinkElement | undefined
        if(!this.subscribeCTAButton) return

        this.subscribeCTAButton.onclick = (event: MouseEvent) => {
            event.stopPropagation()
            UIMainPageRankedActionsController.sendSubscribeCTAAnalyticEvent()
            navigateToPage('/store#membership', true)
        }
    }

    private toggleCKSubscribeCTA(show: boolean) {
        if(!this.subscribeCTAButton) return
        this.subscribeCTAButton.style.display = show ? 'flex' : 'none'
    }

    private setRankedIneligibleSubtext() {
        const gamesLeftToUnlockRanked = UserSessionUtils.gamesLeftToUnlockRanked()
        const gamesLeftText: TranslationObject = {key: 'strings:homePage.ranked.rankedIneligible', options: {count: gamesLeftToUnlockRanked}}
        setElementInnerHTML(this.fourPlayerSpan, gamesLeftText)
        setElementInnerHTML(this.oneVersusOneSpan, gamesLeftText)

        const citiesAndKnightsText: TranslationObject = this.hasAccessToCitiesAndKnights ? gamesLeftText : {key: 'strings:homePage.becomeAMemberToUnlock'}
        setElementInnerHTML(this.citiesAndKnightsSpan, citiesAndKnightsText)
    }

    private populateSignInLinks() {
        const signInText: TranslationObject = {key: 'strings:homePage.ranked.logInToPlayRanked'}
        const fourPlayerAnchor = createLinkIn(this.fourPlayerSpan, '', signInText, '')
        const oneVersusOneAnchor = createLinkIn(this.oneVersusOneSpan, '', signInText, '')
        const citiesAndKnightsAnchor = createLinkIn(this.citiesAndKnightsSpan, '', signInText, '')

        fourPlayerAnchor.onclick = (event: MouseEvent) =>
            UIMainPageRankedActionsController.showLoginPopupOnAnchorClick(event)

        oneVersusOneAnchor.onclick = (event: MouseEvent) =>
            UIMainPageRankedActionsController.showLoginPopupOnAnchorClick(event)

        citiesAndKnightsAnchor.onclick = (event: MouseEvent) =>
            UIMainPageRankedActionsController.showLoginPopupOnAnchorClick(event)
    }

    private static showLoginPopupOnAnchorClick(event: MouseEvent) {
        event.stopPropagation()
        event.preventDefault()
        new ViewPopupLogin()
    }

    private async handleCardClicked(matchmakingType: MatchmakingMatchTypes) {
        Sound.playSound(Sound.SoundType.Click)
        await UIWAdController.initiatePopupActionFromUserState(this.uiManager.adController.adblockState.isAdblockActive)
        this.uiManager.matchmakingController.sendFindGameMessageToServer([matchmakingType], true)
    }

    private populatePlacementGames() {
        this.findGameRankedStates.forEach((findGameRankedState: FindGameRankedState) => {
            if(!findGameRankedState.active) return
            if(!findGameRankedState.rankedState) return

            const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState)
            const placementGamesText = rankedState.getFindGamePopupStatusText()
            switch(findGameRankedState.eloType) {
                case ELOTypeEnum.VisibleRanked1v1: {
                    setElementInnerHTML(this.oneVersusOneSpan, placementGamesText)
                    return
                }
                case ELOTypeEnum.VisibleRanked4PClassic: {
                    setElementInnerHTML(this.fourPlayerSpan, placementGamesText)
                    return
                }
                case ELOTypeEnum.VisibleRankedCitiesAndKnights4P: {
                    const citiesAndKnightsText: TranslationObject = this.hasAccessToCitiesAndKnights ? placementGamesText : {key: 'strings:homePage.becomeAMemberToUnlock'}
                    setElementInnerHTML(this.citiesAndKnightsSpan, citiesAndKnightsText)
                    return
                }
                default:
                    return
            }
        })
    }

    private static sendSubscribeCTAAnalyticEvent() {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.MainPageRankedSubscribeCTA,
        }
        void CA.sendAnalyticEvent(eventData)
    }
}
